import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { Box, Typography } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getLocalCategoriesList } from 'entities/Category/model/selectors/categorySelectors';

import { deriveChangeCatalogItemCategoryState } from '../../lib/deriveChangeCatalogItemCategoryState';
import { resolveSelectionToItemInput } from '../../lib/resolveSelectionToItemInput';
import { ChangeCatalogItemCategoryDialog } from '../ChangeCatalogItemCategoryDialog/ChangeCatalogItemCategoryDialog';

import type {
    CatalogCategoryChangeFollowUp,
    CatalogItemCategoryChangeInput,
} from '../../lib/types';
import type { MappedCatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';

export type ChangeCatalogItemCategoryButtonVisualVariant =
    | 'propertyCatalogToolbar'
    | 'orderRow';

export interface ChangeCatalogItemCategoryButtonProps {
    // This can power proper items (order list or item detail for example)
    items?: CatalogItemCategoryChangeInput[];
    // This powers selections from the Property Catalog bulk actions.
    selectedItems?: Record<string, Partial<MappedCatalogItem>>;
    onAfterCatalogCategoryChange?: CatalogCategoryChangeFollowUp;
    visualVariant?: ChangeCatalogItemCategoryButtonVisualVariant;
    'data-testclass'?: string;
}

export const ChangeCatalogItemCategoryButton = memo(
    ({
        items: itemsProp,
        selectedItems,
        onAfterCatalogCategoryChange,
        visualVariant = 'propertyCatalogToolbar',
        'data-testclass': dataTestClass,
    }: ChangeCatalogItemCategoryButtonProps) => {
        const { t } = useTranslation();
        const localCategories = useSelector(getLocalCategoriesList);
        const [dialogOpen, setDialogOpen] = useState(false);

        const catalogItems = useMemo((): CatalogItemCategoryChangeInput[] => {
            if (itemsProp?.length) {
                return itemsProp;
            }
            return resolveSelectionToItemInput(selectedItems);
        }, [itemsProp, selectedItems]);

        const changeCatalogState = useMemo(
            () =>
                deriveChangeCatalogItemCategoryState(
                    catalogItems,
                    localCategories,
                    t,
                ),
            [catalogItems, localCategories, t],
        );

        if (!changeCatalogState) {
            return null;
        }

        const { useAddCategoryTitle } = changeCatalogState;

        const isToolbar = visualVariant === 'propertyCatalogToolbar';

        const icon = useAddCategoryTitle ? (
            <CreateNewFolderIcon
                sx={{
                    fontSize: '2rem',
                    width: '20px',
                    height: '20px',
                }}
            />
        ) : (
            <DriveFileMoveIcon
                sx={{
                    fontSize: '2rem',
                    width: '20px',
                    height: '20px',
                }}
            />
        );

        return (
            <>
                <Box
                    data-testclass={dataTestClass}
                    display="flex"
                    alignItems="center"
                    onClick={() => setDialogOpen(true)}
                    sx={
                        isToolbar
                            ? (theme) => ({
                                  cursor: 'pointer',
                                  gap: '4px',
                                  padding: '6px 10px',
                                  borderRadius: '4px',
                                  '&:hover': {
                                      backgroundColor: '#E8EEF6',
                                  },
                                  '& .MuiSvgIcon-root': {
                                      color: theme.palette.primary.main,
                                  },
                              })
                            : {
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  height: '32px',
                                  minHeight: '32px',
                                  boxSizing: 'border-box',
                                  padding: '0 10px',
                                  borderRadius: '4px',
                                  '&:hover': {
                                      backgroundColor: '#E8EEF6',
                                  },
                                  '& .MuiSvgIcon-root': {
                                      color: '#3F6EB7',
                                  },
                              }
                    }
                >
                    {icon}
                    <Typography
                        color={isToolbar ? 'primary' : undefined}
                        sx={
                            isToolbar
                                ? {
                                      fontSize: '1.4rem',
                                      fontWeight: 600,
                                      lineHeight: '2.4rem',
                                  }
                                : {
                                      color: '#3F6EB7',
                                      fontSize: '1.3rem',
                                      fontWeight: 500,
                                      lineHeight: '2rem',
                                  }
                        }
                        variant="subtitle2"
                    >
                        {useAddCategoryTitle
                            ? t('Add Category')
                            : t('Edit Category')}
                    </Typography>
                </Box>
                {dialogOpen ? (
                    <ChangeCatalogItemCategoryDialog
                        changeCatalogState={changeCatalogState}
                        onAfterCatalogCategoryChange={
                            onAfterCatalogCategoryChange
                        }
                        onClose={() => setDialogOpen(false)}
                    />
                ) : null}
            </>
        );
    },
);
